import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ProductDetailsDialogComponent } from 'src/app/features/catalogs/components/new-catalog/new-catalog-add-products/selecteable-products-list/product-details-dialog/product-details-dialog.component';
import { IInventoryFilter, IProduct } from 'src/app/features/produits/models/products.models';
import { environment } from 'src/environments/environment';
import { parseInventoryFilterRequest, refillInventoryFilter } from '../../utils/helpers';
import { IPagination } from '../../models/page.model';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { CommonModule } from '@angular/common';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedModule } from '../../shared.module';
import { quantityValidator } from '../../validators/fields-validators';
import { TranslateService } from '@ngx-translate/core';
import { IQuotationProduct } from 'src/app/features/quotations/models/quotations.models';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { ProductCodeService } from 'src/app/core/services/product-code.service';
@Component({
  selector: 'app-selectable-products',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    UiModule
  ],
  templateUrl: './selectable-products.component.html',
  styleUrls: ['./selectable-products.component.scss']
})
export class SelectableProductsComponent implements OnInit {
  @Input() initialSelectedProducts: IProduct[] = [];
  @Input() initialSelectedProductsIds: number[] = [];
  @Input() infoMode: 'brand' | 'quantity' | 'discount' = 'brand';
  @Output() selectedProductsChange = new EventEmitter<IProduct[]>();

  products: IProduct[] = [];
  selectedProducts: IProduct[] = [];

  // Sorting
  activeSortKey: string | null = null;
  activeSortMode: 'asc' | 'desc' | null = null;
  
  // Search
  searchInput = new FormControl('');

  // Filter
  filter: IInventoryFilter = refillInventoryFilter();

  // Pagination
  productsMetadata: IPagination | null = null;
  currentPage = 1;
  pageSize = 50;
  isLoadingMore = false;
  hasMore: boolean = false;
  loading = false;

  baseUrl = environment.BASE_URL_API;
  placeholder = 'assets/images/placeholder.png';
  timestamp = new Date().getTime();
  failedImages = new Set<string>();

  productsForm = new FormGroup({});

  destroy$ = new Subject<void>();

  constructor(
    public dialog: MatDialog, 
    private translateService: TranslateService,
    private productsService: ProductsService,
    public productCode: ProductCodeService
  ) {}

  ngOnInit(): void {
    // Get initial selected products
    if(this.initialSelectedProducts.length > 0) {
      this.selectedProducts = [...this.initialSelectedProducts];
      
      console.log('initialSelectedProducts: ', this.initialSelectedProducts);
      console.log('selectedProducts: ', this.selectedProducts);
    }

    // Subscribe to search input changes
    this.searchInput.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      this.currentPage = 1;
      this.load();
    });

    this.load();
  }

  load() {
    const keyword = this.searchInput.value || undefined;
    const filterRequest = parseInventoryFilterRequest(this.filter);
    
    this.loading = true;
    
    this.productsService.fetchProductsBySearch(
      { number: this.currentPage, size: this.pageSize },
      filterRequest,
      keyword,
      this.initialSelectedProducts.map(product => product.id!) || [],
      false
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        const products: IProduct[] = response.data || [];
        
        if (this.isLoadingMore) {
          this.products = [...this.products, ...products];
          this.isLoadingMore = false;
        } else {
          this.products = products;
        }

        this.productsMetadata = response.metadata || null;

        if(this.productsMetadata) {
          this.hasMore = this.products.length < this.productsMetadata.count;
        } else {
          this.hasMore = false;
        }

        // Handle initial selected products by IDs
        if(this.initialSelectedProductsIds.length > 0) {
          this.selectedProducts = this.products.filter(product => this.initialSelectedProductsIds.includes(product.id!));
        }

        // Create form controls for products
        this.products.forEach((product) => {
          this.createControlsForProduct(product);
        });
        
        // Add validators for initially selected products
        this.selectedProducts.forEach(selectedProduct => {
          this.addValidatorsToProduct(selectedProduct);
        });

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading products:', error);
        this.loading = false;
        this.isLoadingMore = false;
      }
    });
  }

  loadMore() {
    if (!this.productsMetadata) return;
    if (this.products.length >= this.productsMetadata.count) return;

    this.isLoadingMore = true;
    this.currentPage += 1;
    this.load();
  }

  sortByKey(key: string, sortDirection: 'asc' | 'desc'): void {
    if (key === this.activeSortKey && sortDirection === this.activeSortMode) return;

    this.activeSortKey = key;
    this.activeSortMode = sortDirection;
    this.currentPage = 1;
    this.load();
  }

  trackByProductId(_index: number, product: IProduct): number {
    return product.product_id!;
  }

  createControlsForProduct(product: IProduct) {
    if(this.productsForm.contains('product_' + product.product_id!)) return;

    // Always create empty controls without validators initially
    const selectedProduct = this.selectedProducts.find(p => p.product_id === product.product_id);

    if(this.infoMode === 'quantity') {
      this.productsForm.addControl('product_' + product.product_id!, new FormControl(selectedProduct?.quantity || null, [Validators.required, quantityValidator(product as unknown as IQuotationProduct, this.translateService)]));
    } else if(this.infoMode === 'discount') {
      this.productsForm.addControl('product_' + product.product_id!, new FormControl(selectedProduct?.discount || null, [Validators.required, Validators.min(0), Validators.max(100)]));
    } else {
      this.productsForm.addControl('product_' + product.product_id!, new FormControl(null, []));
    }
  }

  getImage(image: string): string {
    return this.baseUrl + image + '?thumbnail=1&timestamp=' + this.timestamp;
  }

  onImageError(imageUrl: string): void {
    this.failedImages.add(imageUrl);
  }

  onUpdateQuantity(event: Event, product: IProduct) {
    const quantity = +(event.target as HTMLInputElement).value;

    
    // If no value dont toggle the product
    if(!quantity) {
      if(this.selectedProducts.some(p => p.product_id === product.product_id)) {
        this.selectedProducts = this.selectedProducts.filter(p => p.product_id !== product.product_id);
      }
    } else {
      if(this.selectedProducts.some(p => p.product_id === product.product_id)) {
        this.selectedProducts = this.selectedProducts.map(p => p.product_id === product.product_id ? { ...p, quantity: quantity } : p);
        product = { ...product, quantity: quantity };
      } else {
        this.selectedProducts.push({ ...product, quantity: quantity });
        product = { ...product, quantity: quantity };
      }
    }

    this.emitSelectedProducts();
  }

  onUpdateDiscount(event: Event, product: IProduct) {
    const discount = +(event.target as HTMLInputElement).value;
    
    // If no value dont toggle the product
    if(!discount) {
      if(this.selectedProducts.some(p => p.product_id === product.product_id)) {
        this.selectedProducts = this.selectedProducts.filter(p => p.product_id !== product.product_id);
      }
    } else {
      if(this.selectedProducts.some(p => p.product_id === product.product_id)) {
        this.selectedProducts = this.selectedProducts.map(p => p.product_id === product.product_id ? { ...p, discount: discount } : p);
        product = { ...product, discount: discount };
      } else {
        this.selectedProducts.push({ ...product, discount: discount });
        product = { ...product, discount: discount };
      }
    }

    this.emitSelectedProducts();
  }

  isAllProductsSelected(): boolean {
    const allProductsIds = this.products.map(
      (product: IProduct) => product.product_id
    );
    return allProductsIds.length
      ? allProductsIds.every((id: number | undefined) => this.selectedProducts.some(p => p.product_id === id))
      : false;
  }

  isPartialSelectedAll(): boolean {
    const allProductsIds = this.products.map(
      (product: IProduct) => product.product_id!
    );
    const selectedCount = allProductsIds.filter((id: number) =>
      this.selectedProducts.some(p => p.product_id === id)
    ).length;
    return selectedCount > 0 && selectedCount < allProductsIds.length;
  }

  isProductSelected(product: IProduct) {
    return this.selectedProducts.some(p => p.product_id === product.product_id)
  }

  isFilterActive(): boolean {
    return parseInventoryFilterRequest(this.filter)?.filters?.length > 0;
  }

  toggleAllProducts(checked: boolean) {
    const allIds = this.products
      .map((product: IProduct) => product);
    this.selectedProducts = checked ? [...allIds] : [];

    if(checked) {
      this.selectedProducts = this.selectedProducts.map((product: IProduct) => {
        const control = this.productsForm.get('product_' + product.product_id!);

        // Set default value based on mode
        if (this.infoMode === 'quantity') {
          product = { 
            ...product, 
            quantity: control && Number(control.getRawValue()) ? Number(control?.getRawValue()) : product.min_qte!
          };
        } else if (this.infoMode === 'discount') {
          const value = control && Number(control.getRawValue()) >= 0 ? Number(control?.getRawValue()) : 0;
          product = { 
            ...product, 
            discount: value
          };
        }

        // Initialize control value when empty
        if(control?.getRawValue() === null) {
          control?.setValue(this.infoMode === 'quantity' ? product.min_qte! : 0);
        }

        return product;
      });
      
      // Add validators for all selected products
      this.selectedProducts.forEach(product => {
        this.addValidatorsToProduct(product);
      });
    } else {
      // Remove validators for all products
      this.selectedProducts.forEach(product => {
        this.removeValidatorsFromProduct(product);
      });
    }

    this.emitSelectedProducts()
  }

  toggleProduct(product: IProduct, checked: boolean) {
    const control = this.productsForm.get('product_' + product.product_id!);

    if (checked) {
      // Add the product to the selected products with appropriate default value
      if (this.infoMode === 'quantity') {
        this.selectedProducts.push({ 
          ...product, 
          quantity: control && Number(control.getRawValue()) ? Number(control?.getRawValue()) : product.min_qte!
        });
      } else if (this.infoMode === 'discount') {
        this.selectedProducts.push({ 
          ...product, 
          discount: control && Number(control.getRawValue()) >= 0 ? Number(control?.getRawValue()) : 0
        });
      } else {
        this.selectedProducts.push({ ...product });
      }

      // Add validators when product is selected
      this.addValidatorsToProduct(product);

      // Initialize control value when empty
      if(control?.getRawValue() === null) {
        control?.setValue(this.infoMode === 'quantity' ? product.min_qte! : 0);
      }
    } else {
      this.selectedProducts = this.selectedProducts.filter(
        (prod: IProduct) => prod.product_id! !== product.product_id!
      );

      // Remove validators when product is deselected
      this.removeValidatorsFromProduct(product);
    }
    this.emitSelectedProducts()
  }

  openDetails(product: IProduct) {
    // Disable for now, as the product dialog does not offer any useful information
    return;
    this.dialog.open(ProductDetailsDialogComponent, {
      data: product,
      minWidth: '600px',
      panelClass: 'custom-side-dialog'
    })
  }

  openFilter() {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      minWidth: '650px',
      width: '80vw',
      height: '80vh',
      data: {
        filter: {...this.filter.productsInfoFilter, ...this.filter.inventoryFilter, ...this.filter.organizeFilter}
      }
    });
    dialogRef.afterClosed().subscribe((filter: IInventoryFilter) => {
      if (filter) {
        this.filter = filter;
        this.currentPage = 1;
        this.load();
      }
    });
  }

  clearFilter() {
    this.filter = refillInventoryFilter();
    this.currentPage = 1;
    this.load();
  }

  addValidatorsToProduct(product: IProduct) {
    const control = this.productsForm.get('product_' + product.product_id!);
    if (control) {
      if (this.infoMode === 'quantity') {
        control.setValidators([Validators.required, quantityValidator(product as unknown as IQuotationProduct, this.translateService)]);
      } else if (this.infoMode === 'discount') {
        control.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      } else {
        control.setValidators([]);
      }
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  removeValidatorsFromProduct(product: IProduct) {
    const control = this.productsForm.get('product_' + product.product_id!);
    if (control) {
      control.setValidators([]);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  emitSelectedProducts() {
    this.selectedProductsChange.emit(this.selectedProducts);
    console.log('selectedProducts: ', this.selectedProducts);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
